import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
  getWalletAccountAddressByType,
} from '@dynamic-labs-sdk/client';
import type { WalletProviderConnectAddress } from '@dynamic-labs-sdk/client/core';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';

import type { BitcoinWalletProvider } from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { getNetworkIdFromNetworkName } from '../../../../utils/getNetworkIdFromNetworkName';
import type { OylBitcoinInjectedProvider } from '../OylBitcoinInjectedProvider.types';

export const OYL_METADATA: WalletProviderMetadata = {
  displayName: 'Oyl Wallet',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#oyl`,
};

type CreatePhantomBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: OylBitcoinInjectedProvider;
};

export const createOylBitcoinWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreatePhantomBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: OYL_METADATA.displayName,
    walletProviderType,
  });

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const connectedAddresses = await injectedProvider.getAddresses();

    const addresses: WalletProviderConnectAddress[] = [];

    const ordinalsAddress = connectedAddresses.taproot;
    if (ordinalsAddress) {
      addresses.push({
        address: ordinalsAddress.address,
        publicKey: ordinalsAddress.publicKey,
        type: 'ordinals',
      });
    }

    const paymentAddress = connectedAddresses.nativeSegwit;
    if (paymentAddress) {
      addresses.push({
        address: paymentAddress.address,
        publicKey: paymentAddress.publicKey,
        type: 'payment',
      });
    }

    return {
      addresses,
    };
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () => {
      const network = await injectedProvider.getNetwork();

      return getNetworkIdFromNetworkName(
        { networkName: network },
        dynamicClient
      );
    };

  // there's no specific method to get connected addresses in Oyl
  // so we check if the selected account is connected and return the addresses
  // if not, we return an empty array
  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const isSelectedAccountConnected = await injectedProvider.isConnected();

      if (!isSelectedAccountConnected) {
        return { addresses: [] };
      }

      // calling connect won't prompt the user to connect again if isConnected is true
      const { addresses } = await connect();
      return { addresses: addresses.map((address) => address.address) };
    };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    addressType,
    protocol,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    let addressToSignWith = walletAccount.address;
    if (addressType) {
      addressToSignWith =
        getWalletAccountAddressByType({
          type: addressType,
          walletAccount,
        }) ?? addressToSignWith;
    }

    const result = await injectedProvider.signMessage({
      address: addressToSignWith,
      message,
      // Oyl Wallet calls it just bip322 instead of bip322-simple
      protocol: protocol === 'bip322-simple' ? 'bip322' : protocol,
    });

    return {
      signature: result.signature,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);

    const { psbt: signedPsbtHex } = await injectedProvider.signPsbt({
      broadcast: false,
      finalize: false,
      psbt: psbtFromBase64.toHex(),
    });

    return { signedPsbt: Psbt.fromHex(signedPsbtHex).toBase64() };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedPsbts: string[] = [];

    for (const request of requests) {
      const signedPsbtResponse = await signPsbt({ request, walletAccount });

      if (signedPsbtResponse) {
        signedPsbts.push(signedPsbtResponse.signedPsbt);
      }
    }

    return {
      signedPsbts,
    };
  };

  return {
    chain,
    connect,
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(OYL_METADATA.displayName),
    key,
    metadata: OYL_METADATA,
    signMessage,
    signPsbt,
    signPsbts,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
